import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Download,
  Eye,
  FileText,
  FolderOpen,
  Image as ImageIcon,
  Loader2,
  Video,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { FileCategoryChip } from '@/components/documents/FileCategoryChip';
import { PatientFileBrowseControls } from '@/components/documents/PatientFileBrowseControls';
import {
  FilePreviewOverlay,
  type FilePreviewSource,
} from '@/components/documents/FilePreviewOverlay';
import { PatientTabIconButton } from '@/components/patient/PatientTabActions';
import { Button } from '@/components/ui/Button';
import {
  useGetDocumentDownloadUrl,
  usePatientDocuments,
  type DocumentRow,
} from '@/hooks/useDocuments';
import { usePatientFileCategories } from '@/hooks/usePatientFileCategories';
import { documentAccessFailureKey } from '@/lib/documentStorageAccess';
import { documentListMeta } from '@/lib/documentSource';
import {
  ALL_FILE_CATEGORIES_ID,
  fileCategoryFilterCounts,
  resolveFileCategory,
} from '@/lib/patientFileCategories';
import { filterPatientFilesForBrowse } from '@/lib/patientFileSearch';
import { readPatientFileSort, sortPatientFiles } from '@/lib/patientFileSort';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { downloadUrlAsFile } from '@/utils/documentDownload';

const ICON = 1.75;

function fileIcon(mimeType: string) {
  if (mimeType.startsWith('video/')) return Video;
  if (mimeType.startsWith('image/')) return ImageIcon;
  return FileText;
}

/**
 * Patient files inside Tidligere notater og filer.
 * Same rows as the patient Filer tab: name, category, type/size/date/source,
 * Vis and Download, and the shared preview. No upload or delete from this column.
 */
export function JournalWritePatientFiles({
  clinicId,
  patientId,
  userId = null,
  active = true,
  onVisibleCount,
}: {
  clinicId: string;
  patientId: string;
  userId?: string | null;
  active?: boolean;
  onVisibleCount?: (count: number | null) => void;
}) {
  const { t } = useTranslation();
  const docs = usePatientDocuments(active ? clinicId : null, active ? patientId : null);
  const categoriesQuery = usePatientFileCategories(active ? clinicId : null);
  const getDownloadUrl = useGetDocumentDownloadUrl();
  const [preview, setPreview] = useState<FilePreviewSource | null>(null);
  const [filter, setFilter] = useState(ALL_FILE_CATEGORIES_ID);
  const [query, setQuery] = useState('');
  const [sort] = useState(() => readPatientFileSort(userId));
  const onVisibleCountRef = useRef(onVisibleCount);
  onVisibleCountRef.current = onVisibleCount;

  const categories = useMemo(() => categoriesQuery.data ?? [], [categoriesQuery.data]);
  const rows = useMemo(() => docs.data ?? [], [docs.data]);
  const counts = fileCategoryFilterCounts(rows, categories);
  const visible = useMemo(
    () =>
      sortPatientFiles(
        filterPatientFilesForBrowse(rows, {
          query,
          categoryId: filter,
          extraText: (doc) => [
            resolveFileCategory(categories, doc.category_id)?.name ??
              t('patients.files.uncategorized'),
            documentListMeta({
              mimeType: doc.mime_type,
              sizeBytes: doc.size_bytes,
              createdAt: doc.created_at,
              documentDate: doc.document_date,
              tags: doc.tags,
              t,
            }),
          ],
        }),
        sort,
      ),
    [categories, filter, query, rows, sort, t],
  );

  useEffect(() => {
    if (!active) return;
    if (docs.isLoading || docs.isError) {
      onVisibleCountRef.current?.(null);
      return;
    }
    onVisibleCountRef.current?.(visible.length);
  }, [active, docs.isError, docs.isLoading, visible.length]);

  const openPreview = (doc: DocumentRow) => {
    setPreview({
      type: 'document',
      documentId: doc.id,
      filename: doc.original_filename,
      mimeType: doc.mime_type,
    });
  };

  const download = async (doc: DocumentRow) => {
    try {
      const url = await getDownloadUrl.mutateAsync({
        documentId: doc.id,
        filename: doc.original_filename,
      });
      await downloadUrlAsFile({
        url,
        mimeType: doc.mime_type,
        filename: doc.original_filename,
      });
    } catch (error) {
      toast.error(t(documentAccessFailureKey(error)));
    }
  };

  return (
    <>
      {preview ? <FilePreviewOverlay source={preview} onClose={() => setPreview(null)} /> : null}
      <div className="jw-files-body">
        {docs.isLoading ? (
          <div className="jw-state" role="status" aria-live="polite">
            <Loader2 className="h-6 w-6 animate-spin" strokeWidth={ICON} aria-hidden />
            {t('app.loading')}
          </div>
        ) : docs.isError ? (
          <div className="jw-state" role="alert">
            <p>{t('documents.load_error')}</p>
            <Button type="button" variant="secondary" onClick={() => void docs.refetch()}>
              {t('app.retry')}
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <div className="jw-state">
            <FolderOpen className="h-7 w-7" strokeWidth={ICON} aria-hidden />
            <p>{t('patients.files.empty')}</p>
            <p className="text-sm text-ds-muted">{t('patients.files.empty_hint')}</p>
          </div>
        ) : (
          <>
            <div className="jw-file-tools">
              <PatientFileBrowseControls
                query={query}
                onQuery={setQuery}
                filter={filter}
                onFilter={setFilter}
                categories={categories}
                counts={counts}
                searchTestId="journal-files-search"
              />
            </div>
            {visible.length === 0 ? (
              <div className="jw-state">
                <p>{t('patients.files.none')}</p>
              </div>
            ) : (
              <ul className="jw-list jw-scroller" aria-label={t('patients.tabs.documents')}>
                {visible.map((doc) => {
                  const Icon = fileIcon(doc.mime_type);
                  const category = resolveFileCategory(categories, doc.category_id);
                  return (
                    <li key={doc.id} className="jw-it">
                      <div className="jw-file-row">
                        <div className="jw-file-row-h">
                          <button
                            type="button"
                            className="jw-file-open"
                            data-testid={`journal-history-file-${doc.id}`}
                            onClick={() => openPreview(doc)}
                          >
                            <Icon className="mt-0.5 h-4 w-4 shrink-0 text-ds-muted" aria-hidden />
                            <span>{doc.original_filename}</span>
                          </button>
                          <PatientTabIconButton
                            label={t('documents.view')}
                            onClick={() => openPreview(doc)}
                          >
                            <Eye className="h-4 w-4" />
                          </PatientTabIconButton>
                          <PatientTabIconButton
                            label={t('documents.download')}
                            onClick={() => void download(doc)}
                            disabled={getDownloadUrl.isPending}
                          >
                            <Download className="h-4 w-4" />
                          </PatientTabIconButton>
                        </div>
                        <span className="pf-file-meta">
                          <span className={cn(TYPE.small, 'pf-file-meta-text text-ds-muted')}>
                            {documentListMeta({
                              mimeType: doc.mime_type,
                              sizeBytes: doc.size_bytes,
                              createdAt: doc.created_at,
                              documentDate: doc.document_date,
                              tags: doc.tags,
                              t,
                            })}
                          </span>
                          <FileCategoryChip
                            className="pf-file-tag"
                            name={category?.name ?? t('patients.files.uncategorized')}
                            colorToken={category?.color_token ?? 'muted'}
                          />
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>
    </>
  );
}
